// components/AuthGuard.jsx
'use client';

import { useEffect, useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { signedInHome } from '@/utils/authRouting';

import { logger } from '@/utils/log';
export default function AuthGuard({ children }) {
    const router = useRouter();
    const pathname = usePathname();
    const { isAuthenticated, loading, currentOrg } = useAuth();
    const [isReady, setIsReady] = useState(false);

    // Define public routes that don't require authentication
    const publicRoutes = ['/login', '/reset-password'];
    const isPublicRoute = publicRoutes.some(route => pathname.startsWith(route));

    // Routes that require auth but not org selection.
    // '/addorg' belongs here: a user creating their first organization has no
    // org selected yet, so requiring one would bounce them back to /memberships.
    const authOnlyRoutes = ['/memberships', '/myaccount', '/addorg'];
    const isAuthOnlyRoute = authOnlyRoutes.some(route => pathname.startsWith(route));

    useEffect(() => {
        // Wait for loading to complete before making decisions
        if (loading) {
            return;
        }

        // Mark as ready once loading is complete
        setIsReady(true);

        // Public routes - no auth needed
        if (isPublicRoute) {
            // If already authenticated and on login page, redirect to appropriate page
            // Same rule the login page applies itself (utils/authRouting).
            if (isAuthenticated && pathname === '/login') {
                router.replace(signedInHome(currentOrg));
            }
            return;
        }

        // Protected routes handling
        if (!isAuthenticated) {
            logger.log('[AuthGuard] Not authenticated, redirecting to login');
            router.replace('/login');
            return;
        }

        // Auth-only routes (like memberships, myaccount) - just need auth, not org
        if (isAuthOnlyRoute) {
            return;
        }

        // Full protected routes - need both auth and org
        if (!currentOrg && pathname !== '/memberships') {
            logger.log('[AuthGuard] No org selected, redirecting to memberships');
            router.replace('/memberships');
            return;
        }
    }, [isAuthenticated, loading, currentOrg, pathname, isPublicRoute, isAuthOnlyRoute, router]);

    // Show loading state while checking auth
    if (loading || (!isReady && !isPublicRoute)) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-yellow-500 border-t-transparent mx-auto" />
                    <p className="mt-4 text-gray-600 text-sm">Loading...</p>
                </div>
            </div>
        );
    }

    // For public routes, always render
    if (isPublicRoute) {
        return <>{children}</>;
    }

    // For auth-only routes, render if authenticated
    if (isAuthOnlyRoute && isAuthenticated) {
        return <>{children}</>;
    }

    // For full protected routes, render if authenticated with org
    if (isAuthenticated && (currentOrg || isAuthOnlyRoute)) {
        return <>{children}</>;
    }

    // Fallback - show nothing while redirecting
    return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
            <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-yellow-500 border-t-transparent mx-auto" />
                <p className="mt-4 text-gray-600 text-sm">Redirecting...</p>
            </div>
        </div>
    );
}